import { StyleSheet, css } from 'aphrodite';
import { type CSSProperties, type FC, useState } from 'react';
import { toast } from 'sonner';
import { FormFieldType } from '../../../extViewmodels';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { type FocusedLeadAttribute, useLeadAttributeOptions, useUpdateLeadAttribute } from '../data';
import { readableInk } from './colorContrast';

/**
 * One editable HubSpot attribute pill ("Label: Value"), mirroring the legacy
 * `LeadAttribute`. Single-select (`Option`) fields open a dropdown of choices —
 * picking one writes via `PUT lead/{companyId}/Lead/Field/{fieldId}` and the
 * lead query refetches; the picked value renders optimistically while the write
 * is in flight. `MultipleOptions` (and other) fields render read-only for now.
 * Unset fields show "Unknown" with the light-orange highlight (#18844).
 *
 * Not built on components-v2 `PillSelect`: that component is modeled on
 * `IAccountTag`/`ITagCategory` with theme pill-color *keys* and a static options
 * array — it can't lazy-load options on open, carries no per-option `hexColor`,
 * and has no per-field write path, none of which fit this flow. Colors come from
 * the scoped `theme/tokens.css` vars (the focused view's token system), not the
 * web `THEME` object.
 */
type LeadAttributePillProps = {
	companyId: string;
	attribute: FocusedLeadAttribute;
};

function pillColors(hexColor: string | undefined, isUnknown: boolean): CSSProperties {
	// Pick readable text per the option color's luminance — HubSpot colors are
	// often pale, so always-white text was unreadable (#18844).
	if (hexColor) return { background: hexColor, color: readableInk(hexColor) };
	if (isUnknown) return { background: 'var(--attr-unknown-bg)', color: 'var(--attr-unknown-ink)' };
	return { background: 'var(--attr-bg)', color: 'var(--attr-ink)' };
}

export const LeadAttributePill: FC<LeadAttributePillProps> = ({ companyId, attribute }) => {
	const [open, setOpen] = useState(false);
	// Picked option shown optimistically while the PUT is in flight, so the rep
	// sees their selection immediately instead of the stale value (#18844 review).
	const [pendingLabel, setPendingLabel] = useState<string | null>(null);
	const editable = attribute.fieldType === FormFieldType.Option;
	// Options load on demand — only once the dropdown opens.
	const { options, isLoading } = useLeadAttributeOptions(companyId, editable ? attribute.id : undefined, open);
	const updateAttribute = useUpdateLeadAttribute();

	const saving = updateAttribute.isPending && pendingLabel !== null;
	const displayValue = saving ? pendingLabel : attribute.value;
	const colors = pillColors(attribute.hexColor, !displayValue);
	const content = (
		<>
			<span className={css(styles.label)}>{attribute.label}:</span>
			<span>{displayValue || 'Unknown'}</span>
		</>
	);

	if (!editable) {
		return (
			<span className={css(styles.pill)} style={colors} title={attribute.label}>
				{content}
			</span>
		);
	}

	const handleSelect = (optionId: string, optionLabel: string) => {
		setOpen(false);
		setPendingLabel(optionLabel);
		updateAttribute.mutate(
			{ companyId, fieldId: attribute.id, value: optionId },
			{
				onError: () => toast.error(`Couldn't update ${attribute.label}`),
				// Drop the optimistic label once the write settles — on success the
				// refetched lead carries the new value; on error we revert.
				onSettled: () => setPendingLabel(null),
			}
		);
	};

	return (
		<Dropdown open={open} onOpenChange={setOpen}>
			<DropdownTrigger
				className={css(styles.pill, styles.pillButton)}
				style={colors}
				disabled={updateAttribute.isPending}
				title={`Edit ${attribute.label}`}
			>
				{content}
				<span className={css(styles.caret)} aria-hidden='true'>
					▾
				</span>
			</DropdownTrigger>
			<DropdownContent>
				{isLoading && <DropdownItem disabled>Loading…</DropdownItem>}
				{!isLoading && options.length === 0 && <DropdownItem disabled>No options</DropdownItem>}
				{options.map(option => (
					<DropdownItem key={option.id} onClick={() => handleSelect(option.id, option.label)}>
						{option.label}
					</DropdownItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
};

const styles = StyleSheet.create({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		height: 24,
		paddingLeft: 10,
		paddingRight: 10,
		// Intentional fully-rounded pill shape (not the 8px theme default).
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		border: 'none',
		whiteSpace: 'nowrap',
	},
	pillButton: {
		cursor: 'pointer',
		fontFamily: 'inherit',
	},
	label: {
		opacity: 0.7,
	},
	caret: {
		fontSize: 9,
		marginLeft: 2,
	},
});
